// Agrupa las 20 entradas del panel de administración (9 de Pulso + 11 de Fundamentales/Brillamos)
// en 3 secciones colapsables: Admin General (lo que hoy es exclusivo de AdminGlobal dentro del
// bloque de Pulso: system/monitor/trace/jobs), Pulso (surveys/templates/analytics/rules/
// notifications, las que también ve AdminPulso/AdminOU) y Brillamos (las 11 secciones de
// Fundamentales). NO cambia ninguna regla de acceso: reutiliza getAccessibleAdminSections y
// getAccessibleRecognitionSections tal cual, solo reparte su resultado en 3 grupos en vez de 1.
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 3.

import { useMemo } from 'react';
import type { AdminSection, RecognitionAdminSection } from '@/auth/accessMatrix';
import { getAccessibleAdminSections, getAccessibleRecognitionSections } from '@/contexts/UserContext';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { ADMIN_SECTIONS } from '@/views/recognition/admin/sections';
import { fundTab, type PanelTab } from '@/contexts/AdminPanelContext';

export interface AdminMenuItem {
  tab: PanelTab;
  icon: string;
  labelKey: string;
  /** Namespace i18n del labelKey; ausente = namespace por defecto (bloque Pulso/General). */
  i18nNamespace?: string;
}

export interface AdminMenuSection {
  id: 'general' | 'pulso' | 'brillamos';
  titleKey: string;
  items: AdminMenuItem[];
}

// 2026-08-08: los catálogos maestros encabezan Admin General; la pantalla propia de permisos de
// Pulso (`rolesPulso`) va en el grupo Pulso. (Gestión de admins globales sigue en la vista lateral.)
const GENERAL_TABS: readonly AdminSection[] = [
  'catalogoFundamentales', 'catalogoOus', 'system', 'monitor', 'trace', 'jobs',
];
const PULSO_TABS: readonly AdminSection[] = [
  'surveys', 'templates', 'analytics', 'rules', 'notifications', 'rolesPulso',
];

// ⏸️ TEMPORAL — secciones de Brillamos ocultas del menú (no eliminadas). El componente, la ruta
// (`/app/admin/<slug>`) y la matriz de accesos siguen intactos; solo se retira la entrada del
// flyout. Para reactivar: vaciar este set (y devolver los conteos 12/6 en useAdminMenuSections.test).
const HIDDEN_BRILLAMOS_SLUGS: ReadonlySet<RecognitionAdminSection> = new Set<RecognitionAdminSection>([
  'notificaciones',
]);

const TAB_DEFINITIONS: Record<AdminSection, { icon: string; labelKey: string }> = {
  catalogoFundamentales: { icon: 'fa-cubes',         labelKey: 'admin.catalogFundTab'    },
  catalogoOus:           { icon: 'fa-sitemap',       labelKey: 'admin.catalogOusTab'     },
  surveys:       { icon: 'fa-clipboard-list', labelKey: 'admin.surveys'       },
  templates:     { icon: 'fa-layer-group',    labelKey: 'admin.templates'     },
  analytics:     { icon: 'fa-chart-bar',      labelKey: 'admin.analyticsTab'  },
  rules:         { icon: 'fa-sliders-h',      labelKey: 'admin.rulesTab'      },
  notifications: { icon: 'fa-bell',           labelKey: 'admin.notifications' },
  rolesPulso:    { icon: 'fa-user-shield',    labelKey: 'admin.rolesPulsoTab' },
  system:        { icon: 'fa-heartbeat',      labelKey: 'admin.systemTab'     },
  monitor:       { icon: 'fa-stream',         labelKey: 'admin.monitorTab'    },
  trace:         { icon: 'fa-route',          labelKey: 'admin.traceTab'      },
  jobs:          { icon: 'fa-sync',           labelKey: 'admin.jobsTab'       },
};

export interface AdminMenuSectionsResult {
  sections: AdminMenuSection[];
  /**
   * BUG QA — "línea blanca" intermitente al abrir el menú: `activeRoles` cae a
   * `[demoUser.role]` (fallback SOLO-Employee en producción, ver `UserContext.DEFAULT_USER`)
   * mientras `useCurrentUser()` todavía no resuelve el perfil real — con ese rol, `sections` sale
   * VACÍO (Employee no ve nada de Admin), y el flyout se renderizaba como una caja sin contenido
   * (el "hilo blanco" reportado) hasta que el perfil real llegaba, unos segundos después, y el
   * menú "aparecía" de golpe. `AdminFlyout` usa este flag para mostrar una carga explícita en vez
   * de una caja vacía que parece rota.
   */
  profileResolved: boolean;
}

export function useAdminMenuSections(): AdminMenuSectionsResult {
  const { activeRoles, permissions, recognitionPermissions, profileResolved } = useVisibleNavLinks();

  const sections = useMemo(() => {
    const accessibleAdmin = getAccessibleAdminSections(activeRoles, permissions, recognitionPermissions);
    const accessibleFund = getAccessibleRecognitionSections(activeRoles, recognitionPermissions);

    const generalItems: AdminMenuItem[] = GENERAL_TABS
      .filter(tab => accessibleAdmin.includes(tab))
      .map(tab => ({ tab, ...TAB_DEFINITIONS[tab] }));

    const pulsoItems: AdminMenuItem[] = PULSO_TABS
      .filter(tab => accessibleAdmin.includes(tab))
      .map(tab => ({ tab, ...TAB_DEFINITIONS[tab] }));

    const brillamosItems: AdminMenuItem[] = ADMIN_SECTIONS
      .filter(section => accessibleFund.includes(section.slug))
      .filter(section => !HIDDEN_BRILLAMOS_SLUGS.has(section.slug)) // ⏸️ TEMPORAL, ver arriba
      .map(section => ({
        tab: fundTab(section.slug),
        icon: section.icon,
        labelKey: section.labelKey,
        i18nNamespace: 'recognition',
      }));

    const result: AdminMenuSection[] = [];
    if (generalItems.length > 0) result.push({ id: 'general', titleKey: 'admin.menu.general', items: generalItems });
    if (pulsoItems.length > 0) result.push({ id: 'pulso', titleKey: 'admin.menu.pulso', items: pulsoItems });
    if (brillamosItems.length > 0) result.push({ id: 'brillamos', titleKey: 'admin.menu.brillamos', items: brillamosItems });
    return result;
  }, [activeRoles, permissions, recognitionPermissions]);

  return { sections, profileResolved };
}
